import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Plus, ChevronRight } from 'lucide-react'
import api from '../../services/api'

const STATUS_BADGE: Record<string, string> = {
  open: 'badge-high', in_progress: 'badge-medium', findings_review: 'badge-medium',
  closed: 'badge-low',
}

const METHODOLOGY_LABEL: Record<string, string> = {
  icam: 'ICAM', '5_why': '5 Why', fishbone: 'Fishbone',
  bow_tie: 'Bow Tie', hfacs: 'HFACS',
}

const CLASS_BADGE: Record<string, string> = {
  standard: 'badge-low', serious: 'badge-high', notifiable: 'badge-critical',
}

export default function InvestigationsList() {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ status: '', classification: '' })

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter.status) params.set('status', filter.status)
      if (filter.classification) params.set('classification', filter.classification)
      const { data: res } = await api.get(`/investigations?${params}`)
      setData(res.data)
      setTotal(res.total)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Investigations</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{total} total</p>
        </div>
        <Link to="/investigations/new" className="btn btn-primary">
          <Plus size={16} />
          New Investigation
        </Link>
      </div>

      <div className="card p-4 flex flex-wrap gap-3">
        <select className="w-auto" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="findings_review">Findings Review</option>
          <option value="closed">Closed</option>
        </select>
        <select className="w-auto" value={filter.classification} onChange={e => setFilter(f => ({ ...f, classification: e.target.value }))}>
          <option value="">All classifications</option>
          <option value="standard">Standard</option>
          <option value="serious">Serious</option>
          <option value="notifiable">Notifiable</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-16">
          <Search size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No investigations found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Start an investigation from an incident or create a standalone investigation</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Number</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Title</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Incident</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Method</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Lead</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Target</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Class</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50 transition-all" style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3">
                    <Link to={`/investigations/${inv.id}`} className="font-mono text-xs font-medium" style={{ color: 'var(--accent)' }}>
                      {inv.investigation_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <Link to={`/investigations/${inv.id}`} className="font-medium line-clamp-2" style={{ color: 'var(--text)' }}>
                      {inv.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {inv.incident_number ? (
                      <Link to={`/incidents/${inv.incident_id}`} style={{ color: 'var(--accent)' }}>{inv.incident_number}</Link>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
                      {METHODOLOGY_LABEL[inv.methodology] || inv.methodology}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{inv.lead_name || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: inv.target_completion && new Date(inv.target_completion) < new Date() && inv.status !== 'closed' ? 'var(--red)' : 'var(--text-secondary)' }}>
                    {inv.target_completion ? new Date(inv.target_completion).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${CLASS_BADGE[inv.classification] || ''} capitalize`}>{inv.classification}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_BADGE[inv.status] || ''} capitalize`}>{inv.status?.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/investigations/${inv.id}`}><ChevronRight size={16} style={{ color: 'var(--text-muted)' }} /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
